import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/helpers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Save, Copy, ChevronDown, Check } from "lucide-react";
import { toast } from "sonner";

const PROFILE_FIELDS = ["Name", "Father Name", "Admission No", "DOB", "Address", "Class", "Section", "Photo"];
const MONTHS = ["April", "May", "June", "July", "August", "September", "October", "November", "December", "January", "February", "March"];
const LABELS = ["Affiliation Code", "Affiliated To", "School Code", "UDISE Code", "Email", "Phone", "Report Card Title", "Student Profile Text", "Academic Text", "Attendance Label", "Grade Label", "Grade Range", "Total Obtained Marks", "Percentage Range", "Result", "Subject", "Obtained Marks in Exam", "Obtained Grade in Exam", "Obtained Grade in a Term/Group", "Obtained Percentage in a Term/Group", "Obtained Total in a Term/Group", "Maximum in a Term/Group", "Subject Group Total", "Total", "Total Marks", "Overall Marks", "Overall Grade", "Performance", "Rank", "Subject-wise Percentage", "Percentage", "Division", "Marksheet Date", "Parents Sign", "Discipline", "Co-Scholastic Areas", "Total Days", "Total Open Days", "Total Present Days", "Attendance (%)", "Instructions Label", "Authority/Principal Sign", "Teacher Sign", "Marksheet Distribution Date", "Teacher Remark Label", "Promoted to Label"];
const HIDE_OPTS = ["Hide Attendance", "Hide Discipline Card", "Hide Co-Scholastic Areas", "Hide Grade Range Row", "Hide Percentage Range Row", "Hide Student Sign", "Hide Parents Sign", "Hide Instructions", "Hide Grade Column from Exams", "Hide Final Grade Column", "Hide Obtained Marks Column", "Hide Total/Maximum Marks Column", "Hide Result/Percentage Column", "Hide Overall Results Row", "Hide Result Row From Exams Column", "Hide Group/Term Name From Exam's Top", "Hide attendance total open/present day", "Hide overall rank", "Hide unconducted exam", "Hide teacher remark", "Hide grade from term/group", "Hide percentage from term/group", "Hide total from term/group", "Hide maximum from term/group", "Hide subject-wise percentage", "Hide principal's sign", "Hide class teacher's sign", "Hide date", "Hide division", "Hide promoted to", "Hide exam total marks", "Hide max marks from Exam header", "Hide marksheet header"];
const STYLE_NUM = [["logo_width", "School logo width"], ["qr_width", "School QR Code width"], ["photo_width", "Student Photo width"], ["school_name_fs", "School name font size"], ["tagline_fs", "School tagline font size"], ["details_fs", "School details font size"], ["headings_fs", "Headings font size"], ["student_fs", "Student details font size"], ["labels_fs", "Marks heading & labels font size"], ["marks_fs", "Marks font size"], ["marks_col_h", "Marks field column height"], ["subject_col_w", "Marks subject column width"], ["section_gap", "Sections vertical space"]];
const STYLE_COLOR = [["header_bg", "Header Background Color"], ["header_text", "Header Text Color"], ["heading_bg", "Heading Background Color"], ["heading_text", "Heading Text Color"]];

const DEFAULT_COLORS = { header_bg: "#1e293b", header_text: "#ffffff", heading_bg: "#f1f5f9", heading_text: "#0f172a" };

const buildDefaults = () => ({
  profile_fields: ["Name", "Father Name", "Admission No", "Class", "Section"],
  attendance_months: [],
  labels: Object.fromEntries(LABELS.map((l) => [l, l])),
  settings: { rank_based_on: "Total Marks", rank_manual: false, rank_sequence: "High to Low", term_wise_grading: false, watermark_visibility: "Show", watermark_width: 300, show_promoted: false },
  passfail: { min_pass_pct: 33, fail_in: "Any 1 subject", considered_as: "Fail", absent_char: "AB", absent_result: "Fail" },
  hide: Object.fromEntries(HIDE_OPTS.map((h) => [h, false])),
  styling: { ...Object.fromEntries(STYLE_NUM.map(([k]) => [k, ""])), ...DEFAULT_COLORS },
  status: "Active",
  orientation: "Portrait",
});

function Section({ title, children, defaultOpen, testid }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50" data-testid={`${testid}-toggle`}>
        {title}<ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-slate-100">{children}</div>}
    </div>
  );
}

function Chips({ options, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const on = selected.includes(o);
        return (
          <button key={o} type="button" onClick={() => onToggle(o)} data-testid={`${testid}-${o}`}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${on ? "bg-indigo-600 border-indigo-600 text-white" : "border-slate-300 text-slate-600 hover:border-indigo-400"}`}>
            {on && <Check size={12} />}{o}
          </button>
        );
      })}
    </div>
  );
}

export default function AdvanceOptionsTab({ session, sessions }) {
  const { user } = useAuth();
  const canManage = hasPerm(user, "manage_exams");
  const [adv, setAdv] = useState(buildDefaults());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const hydrate = (raw) => {
    const d = buildDefaults();
    const a = raw || {};
    return {
      profile_fields: a.profile_fields || d.profile_fields,
      attendance_months: a.attendance_months || d.attendance_months,
      labels: { ...d.labels, ...(a.labels || {}) },
      settings: { ...d.settings, ...(a.settings || {}) },
      passfail: { ...d.passfail, ...(a.passfail || {}) },
      hide: { ...d.hide, ...(a.hide || {}) },
      styling: { ...d.styling, ...(a.styling || {}) },
      status: a.status || d.status,
      orientation: a.orientation || d.orientation,
    };
  };

  const load = useCallback(() => {
    if (!session) return;
    setLoading(true);
    api.get(`/exam-settings?session=${encodeURIComponent(session)}`).then((r) => setAdv(hydrate(r.data.advanced))).catch(() => toast.error("Could not load advance options")).finally(() => setLoading(false));
  }, [session]);
  useEffect(() => { load(); }, [load]);

  const set = (path, val) => setAdv((p) => {
    const n = structuredClone(p);
    const ks = path.split(".");
    let o = n; for (let i = 0; i < ks.length - 1; i++) o = o[ks[i]];
    o[ks[ks.length - 1]] = val; return n;
  });
  const toggleArr = (key, v) => setAdv((p) => ({ ...p, [key]: p[key].includes(v) ? p[key].filter((x) => x !== v) : [...p[key], v] }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/exam-settings/advanced", { session, advanced: adv }); toast.success("Advance options saved"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const copyFrom = async (src) => {
    if (!src || src === session) return;
    try {
      const r = await api.get(`/exam-settings?session=${encodeURIComponent(src)}`);
      if (!r.data.advanced || Object.keys(r.data.advanced).length === 0) { toast.info(`Session ${src} has no advance options to copy.`); return; }
      setAdv(hydrate(r.data.advanced)); toast.success(`Copied advance options from ${src}. Review and Save to apply.`);
    } catch (e) { toast.error("Could not copy settings"); }
  };

  const otherSessions = (sessions || []).filter((s) => s.name !== session);
  const allMonths = adv.attendance_months.length === MONTHS.length;

  if (loading) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>;

  return (
    <div className="space-y-3" data-testid="advance-options-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-slate-600">Marksheet advance options for session <b>{session || "—"}</b> (saved per session).</p>
        <div className="flex items-center gap-2">
          {canManage && otherSessions.length > 0 && (
            <Select onValueChange={copyFrom}>
              <SelectTrigger className="w-52 h-9" data-testid="adv-copy-from"><Copy size={14} className="mr-1 text-slate-400" /><SelectValue placeholder="Copy Settings From…" /></SelectTrigger>
              <SelectContent>{otherSessions.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {canManage && <Button onClick={save} disabled={saving} className="gap-1.5" data-testid="adv-opt-save"><Save size={15} />{saving ? "Saving…" : "Save"}</Button>}
        </div>
      </div>

      <Section title="Student Profile Fields" defaultOpen testid="sec-profile">
        <p className="text-xs text-slate-400 mb-2">Fields to show on the marksheet.</p>
        <Chips options={PROFILE_FIELDS} selected={adv.profile_fields} onToggle={(v) => toggleArr("profile_fields", v)} testid="profile-chip" />
      </Section>

      <Section title="Attendance Months" testid="sec-months">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs text-slate-400">Months to include in the attendance block.</p>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAdv((p) => ({ ...p, attendance_months: allMonths ? [] : [...MONTHS] }))} data-testid="months-select-all">{allMonths ? "Clear all" : "Select all"}</Button>
        </div>
        <Chips options={MONTHS} selected={adv.attendance_months} onToggle={(v) => toggleArr("attendance_months", v)} testid="month-chip" />
      </Section>

      <Section title="Labels" testid="sec-labels">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
          {LABELS.map((l) => (
            <div key={l}>
              <label className="text-[11px] text-slate-500">{l}</label>
              <Input value={adv.labels[l] ?? ""} onChange={(e) => set(`labels.${l}`, e.target.value)} className="h-8 mt-0.5" />
            </div>
          ))}
        </div>
      </Section>

      <Section title="Settings" testid="sec-settings">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label className="text-[11px] text-slate-500">Calculate Rank Based On</label>
            <Select value={adv.settings.rank_based_on} onValueChange={(v) => set("settings.rank_based_on", v)}><SelectTrigger className="h-8 mt-0.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Total Marks">Total Marks</SelectItem><SelectItem value="Percentage">Percentage</SelectItem></SelectContent></Select>
          </div>
          <div><label className="text-[11px] text-slate-500">Rank Generating Sequence</label>
            <Select value={adv.settings.rank_sequence} onValueChange={(v) => set("settings.rank_sequence", v)}><SelectTrigger className="h-8 mt-0.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="High to Low">High to Low</SelectItem><SelectItem value="Low to High">Low to High</SelectItem></SelectContent></Select>
          </div>
          <div><label className="text-[11px] text-slate-500">Watermark Visibility</label>
            <Select value={adv.settings.watermark_visibility} onValueChange={(v) => set("settings.watermark_visibility", v)}><SelectTrigger className="h-8 mt-0.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Show">Show</SelectItem><SelectItem value="Hide">Hide</SelectItem></SelectContent></Select>
          </div>
          <div><label className="text-[11px] text-slate-500">Watermark Width (px)</label><Input type="number" value={adv.settings.watermark_width} onChange={(e) => set("settings.watermark_width", e.target.value)} className="h-8 mt-0.5" /></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer self-end pb-1"><Checkbox checked={adv.settings.rank_manual} onCheckedChange={(c) => set("settings.rank_manual", !!c)} data-testid="set-rank-manual" />Enter Rank manually</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer self-end pb-1"><Checkbox checked={adv.settings.term_wise_grading} onCheckedChange={(c) => set("settings.term_wise_grading", !!c)} />Term-wise grading subject's grade</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer self-end pb-1"><Checkbox checked={adv.settings.show_promoted} onCheckedChange={(c) => set("settings.show_promoted", !!c)} />Show "Promoted To" column</label>
        </div>
      </Section>

      <Section title="Pass / Fail Condition" testid="sec-passfail">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div><label className="text-[11px] text-slate-500">Min. Passing Percentage</label><Input type="number" value={adv.passfail.min_pass_pct} onChange={(e) => set("passfail.min_pass_pct", e.target.value)} className="h-8 mt-0.5" /></div>
          <div><label className="text-[11px] text-slate-500">If a student is fail in</label><Input value={adv.passfail.fail_in} onChange={(e) => set("passfail.fail_in", e.target.value)} className="h-8 mt-0.5" /></div>
          <div><label className="text-[11px] text-slate-500">The student will be considered as</label><Input value={adv.passfail.considered_as} onChange={(e) => set("passfail.considered_as", e.target.value)} className="h-8 mt-0.5" /></div>
          <div><label className="text-[11px] text-slate-500">Character for Absent in Exam</label><Input value={adv.passfail.absent_char} onChange={(e) => set("passfail.absent_char", e.target.value)} className="h-8 mt-0.5" /></div>
          <div><label className="text-[11px] text-slate-500">On absent in an Exam the result should be</label>
            <Select value={adv.passfail.absent_result} onValueChange={(v) => set("passfail.absent_result", v)}><SelectTrigger className="h-8 mt-0.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Fail">Fail</SelectItem><SelectItem value="Absent">Absent</SelectItem><SelectItem value="Pass">Pass</SelectItem></SelectContent></Select>
          </div>
        </div>
      </Section>

      <Section title="Marksheet Options (Hide)" testid="sec-hide">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-y-2 gap-x-4">
          {HIDE_OPTS.map((h) => (
            <label key={h} className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={adv.hide[h]} onCheckedChange={(c) => set(`hide.${h}`, !!c)} />{h}</label>
          ))}
        </div>
      </Section>

      <Section title="Size & Styling" testid="sec-style">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {STYLE_NUM.map(([k, lbl]) => (
            <div key={k}><label className="text-[11px] text-slate-500">{lbl}</label><Input type="number" value={adv.styling[k] ?? ""} onChange={(e) => set(`styling.${k}`, e.target.value)} className="h-8 mt-0.5" /></div>
          ))}
          {STYLE_COLOR.map(([k, lbl]) => (
            <div key={k}><label className="text-[11px] text-slate-500">{lbl}</label>
              <div className="flex items-center gap-2 mt-0.5">
                <input type="color" value={adv.styling[k] || "#ffffff"} onChange={(e) => set(`styling.${k}`, e.target.value)} className="h-8 w-12 rounded border border-slate-300 cursor-pointer" />
                <Input value={adv.styling[k] ?? ""} onChange={(e) => set(`styling.${k}`, e.target.value)} className="h-8" />
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Status & View Mode" testid="sec-status">
        <div className="flex flex-wrap gap-10">
          <div>
            <p className="text-[11px] text-slate-500 mb-1">Marksheet Status</p>
            <div className="flex gap-4">
              {["Active", "Inactive"].map((s) => (
                <label key={s} className="flex items-center gap-1.5 text-sm cursor-pointer"><input type="radio" name="mk-status" checked={adv.status === s} onChange={() => set("status", s)} data-testid={`status-${s}`} />{s}</label>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[11px] text-slate-500 mb-1">View / Download Mode</p>
            <div className="flex gap-4">
              {["Portrait", "Landscape"].map((o) => (
                <label key={o} className="flex items-center gap-1.5 text-sm cursor-pointer"><input type="radio" name="mk-orient" checked={adv.orientation === o} onChange={() => set("orientation", o)} data-testid={`orient-${o}`} />{o}</label>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
